import { type ReactNode } from 'react'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Icon } from '@/components/Icon'
import { Button } from '@/components/Button'
import { StyledLink } from '@/components/Link'
import { externalLinks } from '@/utils/link'
import styles from './HandoffDialog.module.css'

export interface HandoffDialogProps {
  isOpen: boolean
  onGetStartedPress?: () => unknown
  isCancelAllowed?: boolean
  onCancelPress?: () => unknown
  partnerName: string
  partnerIconUrl?: string | null
  testId?: string
  getStartedButtonTestId?: string
  cancelButtonTestId?: string
}

/**
 * Dialog providing info about the partnership between Taxfyle
 * and the integrator.
 */
export function HandoffDialog({
  isOpen,
  onGetStartedPress,
  onCancelPress,
  isCancelAllowed,
  partnerIconUrl,
  partnerName,
  testId,
  getStartedButtonTestId,
  cancelButtonTestId,
}: HandoffDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onGetStartedPress}
      isDismissable={false}
      isKeyboardDismissDisabled={false}
      size="lg"
      isUsingDialogScrolling
      testId={testId}
    >
      <ModalDialogHeader>
        <PartnershipHeader
          partnerName={partnerName}
          partnerIconUrl={partnerIconUrl}
        />
        <ModalDialogTitle>
          <span className={styles.title}>
            Expert tax filing that gets you everything you're owed
          </span>
        </ModalDialogTitle>
      </ModalDialogHeader>
      <ModalDialogContent spacing="compact" fade="none">
        <ul className={styles.bullets}>
          <BulletPoint>
            <span className="text-strong">Accurate and secure tax filing</span>{' '}
            by licensed CPAs and EAs.
          </BulletPoint>
          <BulletPoint>
            <span className="text-strong">Maximize your refund</span> with
            expert guidance every step of the way.
          </BulletPoint>
          <BulletPoint>
            <span className="text-strong">Personalized tax support</span> with
            no hidden fees.
          </BulletPoint>
        </ul>
      </ModalDialogContent>
      <ModalDialogFooter compact>
        <ModalDialogActions>
          <Button
            label="Continue"
            variant="default"
            onPress={onGetStartedPress}
            testId={getStartedButtonTestId}
          />
          {isCancelAllowed && (
            <Button
              label="Cancel"
              variant="outline"
              onPress={onCancelPress}
              testId={cancelButtonTestId}
            />
          )}
        </ModalDialogActions>
        <div className={styles.legal}>
          By continuing, you acknowledge that you have reviewed and agree to
          Taxfyle's{' '}
          <StyledLink
            href={externalLinks.termsOfUse()}
            variant="inherit"
            target="_blank"
          >
            Terms of Use
          </StyledLink>{' '}
          and{' '}
          <StyledLink
            href={externalLinks.privacyPolicy()}
            variant="inherit"
            target="_blank"
          >
            Privacy Policy
          </StyledLink>
          .
        </div>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

export interface LogoProps {
  src: string
  alt: string
}

/**
 * Logo container for partner and Taxfyle logos
 */
export function Logo({ src, alt }: LogoProps) {
  return (
    <div className={styles.logoContainer}>
      <img className={styles.logoIcon} src={src} alt={alt} />
    </div>
  )
}

/**
 * Animated chevron sequence
 */
export function ChevronSequence() {
  const chevron = (
    <span className={styles.chevron}>
      <Icon name="chevron" />
    </span>
  )

  return (
    <div className={styles.chevrons}>
      {chevron}
      {chevron}
      {chevron}
    </div>
  )
}

export interface PartnershipHeaderProps {
  partnerName: string
  partnerIconUrl?: string | null
}

/**
 * Partnership header showing partner and Taxfyle logos with chevrons
 */
export function PartnershipHeader({
  partnerName,
  partnerIconUrl: _,
}: PartnershipHeaderProps) {
  return (
    <div className={styles.partnershipHeader}>
      {/* TODO: Delete if they don't change their minds again within a couple of months lmao */}
      {/*<div className={styles.partnershipHeaderStrip}>*/}
      {/*  {partnerIconUrl && (*/}
      {/*    <>*/}
      {/*      <Logo src={partnerIconUrl} alt={`${partnerName} logo`} />*/}
      {/*      <ChevronSequence />*/}
      {/*    </>*/}
      {/*  )}*/}

      {/*  <Logo src={taxfyleLogo} alt="Taxfyle logo" />*/}
      {/*</div>*/}
      <div className={styles.partnershipHeaderCopy}>
        {partnerName} works with Taxfyle to help you file your taxes.
      </div>
    </div>
  )
}

export interface BulletPointProps {
  children: ReactNode
}

/**
 * Bullet point list item with icon and text
 */
export function BulletPoint({ children }: BulletPointProps) {
  return (
    <li>
      <Icon sm variant="active" name="radio-check" />
      <span>{children}</span>
    </li>
  )
}
